import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, LogIn, Clock, UserX } from 'lucide-react';

export const LoginPage = () => {
  const { login, logout, isAuthenticated, isAuthorized, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authorization...</p>
        </div>
      </div>
    );
  }

  // User is authenticated but not authorized
  if (isAuthenticated && !isAuthorized) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-card/80 backdrop-blur-sm border-border/50">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[rgb(var(--warning))]/10 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-[rgb(var(--warning))]" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Access Pending
            </h1>
            <p className="text-muted-foreground mb-6">
              Hello {user?.name || user?.email},<br />
              Your account is authenticated but you don't have access to this dashboard yet.
            </p>
            <div className="bg-muted/30 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-muted-foreground">
                <strong>Email:</strong> {user?.email}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Please contact the administrator to request access.
              </p>
            </div>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
                className="w-full"
              >
                Check Again
              </Button>
              <Button 
                variant="ghost" 
                onClick={logout}
                className="w-full gap-2"
                data-testid="switch-account-btn"
              >
                <UserX className="w-4 h-4" />
                Sign in with different account
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Not authenticated - show login
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 bg-card/80 backdrop-blur-sm border-border/50">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Executive Dashboards
          </h1>
          <p className="text-muted-foreground mb-8">
            Sign in with your Microsoft work account to access your dashboard.
          </p>
          
          <Button 
            onClick={login}
            className="w-full gap-2"
            size="lg"
            data-testid="login-btn"
          >
            <LogIn className="w-5 h-5" />
            Sign in with Microsoft
          </Button>

          <p className="text-xs text-muted-foreground mt-6">
            Only authorized users can access this dashboard.
            Contact your administrator if you need access.
          </p>
        </div>
      </Card>
    </div>
  );
};
